import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/shared/api/client';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { useAuthStore } from '@/shared/store/authStore';
import { printRecoveryCodes } from './printRecoveryCodes';

type StatusResp = { enabled: boolean; recovery_codes_left: number };
type SetupResp = { qr: string; otpauth: string; secret: string };
type VerifyResp = { enabled: boolean; recovery_codes: string[] };
type CodesResp = { recovery_codes: string[] };

type PageProps = { embedded?: boolean };

const errText = (e: unknown, map: Record<string, string>, fallback: string) =>
  (e instanceof ApiError ? (map[e.code] || e.message || fallback) : fallback);

// Shown exactly once: after enabling 2FA and after "Generate new codes".
function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const account = useAuthStore((s) => s.user?.email);
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
      <div className="text-[0.8125rem] font-semibold text-amber-900">Save your recovery codes now</div>
      <p className="text-[0.75rem] text-amber-900">
        Each code signs you in once if you lose your authenticator. They are shown only this once;
        store them in your password manager or print them.
      </p>
      <ul className="grid grid-cols-2 gap-1 font-numeric text-body">
        {codes.map((c) => <li key={c} className="bg-surface border border-amber-200 rounded px-2 py-1">{c}</li>)}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => printRecoveryCodes(codes, account)}>Print or save as PDF</Button>
        <Button variant="secondary" onClick={onDone}>I have saved these codes</Button>
      </div>
    </div>
  );
}

export function SecurityPage({ embedded = false }: PageProps = {}) {
  const qc = useQueryClient();
  const [setupData, setSetupData] = useState<SetupResp | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disablePw, setDisablePw] = useState('');
  const [disableOtp, setDisableOtp] = useState('');
  const [regenOtp, setRegenOtp] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const status = useQuery({
    queryKey: ['auth', 'totp', 'status'],
    queryFn: () => api.get<StatusResp>('/auth/totp/status'),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['auth', 'totp', 'status'] });

  const setup = useMutation({
    mutationFn: () => api.post<SetupResp>('/auth/totp/setup'),
    onSuccess: (d) => { setSetupData(d); setMsg(null); setVerifyCode(''); },
    onError: (e) => setMsg({ kind: 'err', text: errText(e, {}, 'Failed to start setup') }),
  });

  const verify = useMutation({
    mutationFn: () => api.post<VerifyResp>('/auth/totp/verify', { token: verifyCode }),
    onSuccess: (d) => {
      setSetupData(null); setVerifyCode(''); setCodes(d.recovery_codes || []); refresh();
      setMsg({ kind: 'ok', text: 'Two-factor authentication enabled. Your other sessions have been signed out.' });
    },
    onError: (e) => setMsg({ kind: 'err', text: errText(e, { invalid_code: 'Code is incorrect. Try again.' }, 'Verification failed.') }),
  });

  const disable = useMutation({
    mutationFn: () => api.post('/auth/totp/disable', { password: disablePw, otp: disableOtp }),
    onSuccess: () => {
      setDisablePw(''); setDisableOtp(''); setCodes(null); refresh();
      setMsg({ kind: 'ok', text: 'Two-factor authentication disabled.' });
    },
    onError: (e) => setMsg({ kind: 'err', text: errText(e, {
      invalid_password: 'Password is incorrect.',
      totp_invalid: 'That code is not valid (each code can be used once).',
      otp_required: 'Enter your authenticator or recovery code.',
    }, 'Disable failed.') }),
  });

  const regenerate = useMutation({
    mutationFn: () => api.post<CodesResp>('/auth/totp/recovery-codes', { otp: regenOtp }),
    onSuccess: (d) => { setRegenOtp(''); setCodes(d.recovery_codes); refresh(); setMsg(null); },
    onError: (e) => setMsg({ kind: 'err', text: errText(e, { totp_invalid: 'That code is not valid (each code can be used once).' }, 'Could not generate new codes.') }),
  });

  const enabled = !!status.data?.enabled;
  const left = status.data?.recovery_codes_left ?? 0;

  return (
    <div>
      {!embedded && <PageHeader title="Security" description="Protect your account with two-factor authentication." />}

      <div className={embedded ? 'space-y-5' : 'grid lg:grid-cols-2 gap-5'}>
        <Card title="Two-Factor Authentication">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[0.8125rem] text-primary-text font-semibold">TOTP (Google Authenticator / Authy)</div>
                <div className="text-[0.75rem] text-muted">Time-based 6-digit codes from your authenticator app.</div>
              </div>
              {enabled ? <Badge tone="success">Enabled</Badge> : <Badge tone="default">Disabled</Badge>}
            </div>

            {codes && <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />}

            {!enabled && !setupData && (
              <div>
                <p className="text-[0.8125rem] text-secondary mb-3">
                  Install Google Authenticator, Authy, 1Password, or any compatible TOTP app. Then click below to generate your QR code.
                </p>
                <Button onClick={() => setup.mutate()} loading={setup.isPending}>Set up TOTP</Button>
              </div>
            )}

            {!enabled && setupData && (
              <div className="space-y-3">
                <div className="rounded-xl border border-border bg-surface p-4 flex items-center gap-4">
                  <img src={setupData.qr} alt="TOTP QR" width={160} height={160} className="rounded-lg border border-border-light bg-white" />
                  <div className="text-[0.75rem] space-y-2 min-w-0">
                    <div>
                      <div className="text-muted uppercase tracking-wide text-[0.625rem] font-semibold mb-0.5">Scan with your app</div>
                      <div className="text-secondary">or enter the key manually:</div>
                    </div>
                    <code className="block bg-slate-100 rounded-md px-2 py-1.5 text-[0.6875rem] font-numeric break-all">{setupData.secret}</code>
                  </div>
                </div>
                <FormField label="Enter the 6-digit code from your app" required>
                  <Input value={verifyCode} onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" placeholder="123456" />
                </FormField>
                <div className="flex gap-2">
                  <Button onClick={() => verify.mutate()} loading={verify.isPending} disabled={verifyCode.length !== 6}>Verify & enable</Button>
                  <Button variant="secondary" onClick={() => { setSetupData(null); setVerifyCode(''); }}>Cancel</Button>
                </div>
              </div>
            )}

            {enabled && (
              <div className="space-y-4">
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-[0.75rem] text-emerald-900">
                  Your account is protected with TOTP. You will be asked for a code on future logins. You have {left} recovery codes left.
                </div>

                <div className="space-y-2">
                  <div className="text-[0.8125rem] font-semibold text-primary-text">Recovery codes</div>
                  <p className="text-[0.75rem] text-muted">Lost the sheet? Generating a new set voids every old code. Enter a current authenticator code to confirm.</p>
                  <div className="flex gap-2 items-end">
                    <FormField label="Authenticator code">
                      <Input aria-label="Authenticator code for new recovery codes" value={regenOtp} inputMode="numeric" placeholder="123456"
                             onChange={(e) => setRegenOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} />
                    </FormField>
                    <Button variant="secondary" onClick={() => regenerate.mutate()} loading={regenerate.isPending} disabled={regenOtp.length !== 6}>
                      Generate new codes
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-[0.8125rem] font-semibold text-primary-text">Disable</div>
                  <p className="text-[0.75rem] text-muted">Disabling needs your password and a current authenticator code or one of your recovery codes.</p>
                  <FormField label="Your password" required>
                    <Input aria-label="Your password" type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} autoComplete="current-password" />
                  </FormField>
                  <FormField label="Authenticator or recovery code" required>
                    <Input aria-label="Authenticator or recovery code" value={disableOtp} placeholder="123456 or xxxxx-xxxxx"
                           onChange={(e) => setDisableOtp(e.target.value.toLowerCase().replace(/[^0-9a-z-]/g, '').slice(0, 11))} />
                  </FormField>
                  <Button variant="danger" onClick={() => disable.mutate()} loading={disable.isPending} disabled={!disablePw || !disableOtp}>
                    Disable TOTP
                  </Button>
                </div>
              </div>
            )}

            {msg && (
              <div className={`text-[0.75rem] ${msg.kind === 'ok' ? 'text-emerald-700' : 'text-danger'}`}>{msg.text}</div>
            )}
          </div>
        </Card>

        <Card title="Good security habits">
          <ul className="text-[0.8125rem] text-secondary space-y-2 list-disc pl-5">
            <li>Use a password manager. Passwords need at least 12 characters and must not be a common word or your email; long passphrases are best.</li>
            <li>Enable TOTP on every account you can, and keep the recovery codes somewhere safe.</li>
            <li>You are signed out after 30 minutes of inactivity and after 12 hours at most.</li>
            <li>When you change your password, every other active session is signed out automatically.</li>
            <li>If you lose your authenticator and your recovery codes, ask your company admin to reset two-factor authentication for your account; your password stays as it is.</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
